import { useState } from "react";
import axios from "axios";
import { doc, setDoc } from "firebase/firestore";
import { db } from "../../utils/firebase";
import { P } from "../Typography";
import IntentInput from "./IntentInput";
import SourceSelector from "./SourceSelector";
import RankingSelector from "./RankingSelector";
import MetadataEditor from "./MetadataEditor";
import Header from "../Header";

const FeedBuilderUI = ({ credentials, setCredentials, onToggleUI }) => {
  const [feedBlueprint, setFeedBlueprint] = useState({});
  const [feedMetadata, setFeedMetadata] = useState("");
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState("");
  const [deployedFeedUri, setDeployedFeedUri] = useState("");
  const [deployedFeedUrl, setDeployedFeedUrl] = useState("");
  const [deployedFeedId, setDeployedFeedId] = useState("");
  const [deploySuccess, setDeploySuccess] = useState(false);

  const handleDeploy = async () => {
    if (!feedMetadata || !feedBlueprint) return;
    setLoading(true);
    setProgress(0);
    setError("");
    setDeploySuccess(false);
    setDeployedFeedUrl("");

    try {
      const interval = setInterval(() => {
        setProgress((prev) => (prev < 95 ? prev + 5 : prev));
      }, 2000);

      const postBody = {
        handle: credentials.handle,
        password: credentials.password,
        hostname:
          process.env.REACT_APP_BLUESKY_FEED_MANAGER_API.split(
            "https://",
          )[1].split("/api")[0],
        record_name: feedMetadata.record_name,
        display_name: feedMetadata.display_name,
        description: feedMetadata.description,
        blueprint: feedBlueprint,
      };

      const res = await axios.post(
        process.env.REACT_APP_BLUESKY_FEED_MANAGER_API,
        postBody,
        {
          headers: {
            "Content-Type": "application/json",
            "x-api-key": process.env.REACT_APP_FEED_API_KEY,
          },
        },
      );

      clearInterval(interval);
      setProgress(100);

      const feedUri = res.data.uri;
      const feedUrl = `https://bsky.app/profile/${feedUri.split("/")[2]}/feed/${
        feedUri.split("/")[4]
      }`;
      const feedId = `${feedUri.split("/")[2]}~${feedUri.split("/")[4]}`;

      setDeployedFeedUri(feedUri);
      setDeployedFeedUrl(feedUrl);
      setDeployedFeedId(feedId);

      setDeploySuccess(true);

      await setDoc(
        doc(db, process.env.REACT_APP_FIREBASE_FIRESTORE_COLLECTION, feedId),
        feedBlueprint,
      );
    } catch (err) {
      console.error("Failed to deploy feed:", err);
      setError("Failed to deploy feed. Please try again.");
    } finally {
      setTimeout(() => setLoading(false), 500);
    }
  };

  return (
    <div className="app-container">
      <div className="ui-col">
        <Header
          setCredentials={setCredentials}
          handle={credentials?.handle}
          setFeedBlueprint={setFeedBlueprint}
          setFeedMetadata={setFeedMetadata}
          onToggleUI={onToggleUI}
          uiMode="bonsai2"
        />
        <IntentInput
          setFeedBlueprint={setFeedBlueprint}
          setFeedMetadata={setFeedMetadata}
        />

        {!!feedBlueprint && !!feedMetadata && (
          <div>
            <MetadataEditor
              feedMetadata={feedMetadata}
              setFeedMetadata={setFeedMetadata}
            />
            <SourceSelector
              feedBlueprint={feedBlueprint}
              setFeedBlueprint={setFeedBlueprint}
            />
            <SourceSelector
              forPreferences={false}
              feedBlueprint={feedBlueprint}
              setFeedBlueprint={setFeedBlueprint}
            />
            <RankingSelector
              feedBlueprint={feedBlueprint}
              setFeedBlueprint={setFeedBlueprint}
            />

            <div className="textarea-container">
              <button
                className="deploy-btn"
                onClick={handleDeploy}
                disabled={loading}
              >
                {loading ? "Deploying..." : "Deploy!"}
              </button>
            </div>

            {error && <div className="error">{error}</div>}

            {loading && (
              <div className="loading-overlay">
                <div className="loading-content">
                  <P
                    className="loading-text"
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    🚀 Deploying your feed!
                  </P>
                  <P
                    className="loading-text"
                    style={{ fontSize: "14px", marginBottom: "1rem" }}
                  >
                    This usually takes a few seconds...
                  </P>

                  <div className="progress-container">
                    <div
                      className="progress-bar"
                      style={{ width: `${progress}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            {deploySuccess && deployedFeedUrl && (
              <div className="loading-overlay">
                <div
                  className="loading-content"
                  style={{ position: "relative" }}
                >
                  <button
                    onClick={() => setDeploySuccess(false)}
                    className="close-icon"
                    aria-label="Close"
                  >
                    ✕
                  </button>

                  <P
                    className="loading-text"
                    style={{
                      fontSize: "20px",
                      fontWeight: "600",
                      marginBottom: "1rem",
                    }}
                  >
                    🦋 Feed deployed successfully!
                  </P>
                  <P
                    className="loading-text"
                    style={{ fontSize: "14px", marginBottom: "1rem" }}
                  >
                    You can check out your new feed here:
                  </P>

                  <small className="success-message">
                    <a
                      href={deployedFeedUrl}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        width: "75%",
                        display: "inline-block",
                        overflowWrap: "anywhere",
                        color: "#1255ad",
                        marginBottom: "10px",
                      }}
                    >
                      {deployedFeedUrl}
                    </a>
                  </small>

                  <div className="subtext">
                    <small>
                      <code>ID: {feedMetadata.id || deployedFeedId}</code>
                    </small>
                  </div>
                  <div className="subtext">
                    <small>(auto-generated, read-only)</small>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedBuilderUI;
